import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import {
  Search, Building, User, Mail, CreditCard, X,
  AlertCircle, CheckCircle, Loader2, Shield, MapPin,
  UserPlus, Trash2, ChevronDown, ChevronUp, Calendar, Home,
} from "lucide-react";

import connectionService from "../services/connectionService";
import companiesHouseService from "../services/companiesHouseService";
import bankValidationService from "../services/bankValidationService";
import TeamService from "../services/teamService";
import { mergeNonEmpty } from "../utils/mergeNonEmpty";

// ── Safe-merge helper ──────────────────────────────────────────────────────
// Merges `incoming` on top of `prev`, but ONLY for keys whose incoming value
// is actually present (not null/undefined/empty-string). This stops partial
// or thin API responses (Companies House lookups, save responses, records
// fetched for edit/view) from wiping out fields that were already filled in
// on the form just because the response didn't return them, or returned
// them as null. This is the root cause of email / bank details / addresses
// getting blanked out after Save, after Companies House autofill, or after
// reopening a record.


// ── Partner / Director sub-form ───────────────────────────────────────────────
const PartnerCard = ({ partner, index, onChange, onRemove, canRemove, readOnly }) => {
  const [collapsed, setCollapsed] = useState(false);

  const handle = (field, value) =>
    onChange(index, { ...partner, [field]: value });

  // ── Read-only display version ──────────────────────────────────────────────
  if (readOnly) {
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
          onClick={() => setCollapsed((c) => !c)}
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
              {index + 1}
            </div>
            <span className="font-medium text-gray-800 text-sm">
              {partner.name?.trim() || `Partner / Director ${index + 1}`}
            </span>
            {partner.position && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {partner.position}
              </span>
            )}
          </div>
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronUp   className="w-4 h-4 text-gray-400" />}
        </div>

        {!collapsed && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Full Name</p>
                <p className="text-sm font-medium text-gray-800">{partner.name || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Position / Role</p>
                <p className="text-sm font-medium text-gray-800">{partner.position || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Date of Birth</p>
                <p className="text-sm font-medium text-gray-800">
                  {partner.dob
                    ? new Date(partner.dob.toString().slice(0, 10) + "T12:00:00").toLocaleDateString("en-GB")
                    : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email Address</p>
                <p className="text-sm font-medium text-gray-800">{partner.email || "—"}</p>
              </div>
            </div>
            <div className="md:col-span-2 flex items-start gap-2">
              <Home className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Private Address</p>
                <p className="text-sm font-medium text-gray-800 whitespace-pre-line">
                  {partner.private_address || "—"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Postcode</p>
                <p className="text-sm font-medium text-gray-800">{partner.postcode || "—"}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Editable version ───────────────────────────────────────────────────────
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Card header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
            {index + 1}
          </div>
          <span className="font-medium text-gray-800 text-sm">
            {partner.name?.trim() || `Partner / Director ${index + 1}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(index); }}
              className="text-red-500 hover:text-red-700 transition-colors p-1 rounded"
              title="Remove partner"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {collapsed
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronUp   className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Card body */}
      {!collapsed && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              value={partner.name || ""}
              onChange={(e) => handle("name", e.target.value)}
              placeholder="Full legal name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Position / Role</label>
            <input
              type="text"
              value={partner.position || ""}
              onChange={(e) => handle("position", e.target.value)}
              placeholder="e.g. Director, Partner"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
            <input
              type="date"
              value={partner.dob ? partner.dob.toString().slice(0, 10) : ""}
              onChange={(e) => handle("dob", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={partner.email || ""}
              onChange={(e) => handle("email", e.target.value)}
              placeholder="partner@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Private Address *</label>
            <textarea
              value={partner.private_address || ""}
              onChange={(e) => handle("private_address", e.target.value)}
              rows={3}
              placeholder="Full home / private address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
              type="text"
              value={partner.postcode || ""}
              onChange={(e) => handle("postcode", e.target.value)}
              placeholder="e.g. SS1 1AA"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Default blank partner ─────────────────────────────────────────────────────
const blankPartner = () => ({
  name: "", position: "", dob: "", email: "", private_address: "", postcode: "",
});

// ── Main component ────────────────────────────────────────────────────────────
const AddConnection = ({ mode, initialData, onSaved, onClose: onCloseProp }) => {

  const [showModal, setShowModal]       = useState(true);
  const [loading, setLoading]           = useState(false);
  const [errors, setErrors]             = useState({});
  const [notification, setNotification] = useState(null);

  const [teamMembers, setTeamMembers]               = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const userId   = localStorage.getItem("userId");
  const userName = localStorage.getItem("userName");

  const [serviceTypes, setServiceTypes] = useState([]);
  const [numberTypes, setNumberTypes]   = useState([]);

  const [companySearchTerm, setCompanySearchTerm]       = useState("");
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [isSearchingCompany, setIsSearchingCompany]     = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown]   = useState(false);
  const [companySearchMode, setCompanySearchMode]       = useState("name");
  const companySearchRef = useRef(null);

  const [bankValidation, setBankValidation] = useState({
    sortCode:           { isValid: null, loading: false, data: null, error: null },
    accountNumber:      { isValid: null, loading: false, data: null, error: null },
    combinedValidation: { isValid: null, loading: false, data: null, error: null },
  });
  const [validationDebounce, setValidationDebounce] = useState(null);

  const [formData, setFormData] = useState({
    id: "",
    salesman_id: userId ?? "",
    salesman_name: userName ?? "",
    regulation_status: "LIMITED",
    installation_address: "",
    contract_date: new Date().toISOString().split("T")[0],
    company_reg: "",
    md_name: "",
    position: "",
    company_name: "",
    registered_address_line_1: "",
    registered_address_line_2: "",
    registered_address_line_3: "",
    registered_address_line_4: "",
    postcode: "",
    director_email: "",
    billing_email: "",
    bank_name: "",
    bank_address: "",
    bank_postcode: "",
    account_holder: "",
    account_number: "",
    sort_code: "",
    isdn2e_channels_required: 0,
    isdn30e_channels_required: 0,
    analogue_channels_required: 0,
    partners: [blankPartner()],
    services: [{
      id: 1, description: "", number_type_id: 1, connection_option_id: 1,
      existing_tel_no: "", existing_lines: "", postcode_of_homeworker: "",
    }],
  });

  // ── Populate form when editing / viewing ────────────────────────────────────
  // Keyed off initialData?.id (not the object reference) so that:
  //  - reopening the modal for the SAME record but with a freshly-fetched
  //    object (new reference, same id) still works because we spread the
  //    latest initialData in every time the effect runs
  //  - the effect reliably refires whenever you switch to a DIFFERENT record
  //  - if the modal is reused for "create new" (initialData undefined/null)
  //    right after viewing/editing a record, we don't leave stale data behind
  //
  // IMPORTANT FIX: we use mergeNonEmpty instead of a raw spread here. If the
  // record fetched for edit/view is missing a field (e.g. the list endpoint
  // returns a slimmer object than the detail endpoint, or a field comes back
  // null because a relation wasn't loaded), a raw `{...prev, ...initialData}`
  // would blank that field out on screen. mergeNonEmpty only overwrites
  // fields that actually have a value in initialData.
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => {
        const merged = mergeNonEmpty(prev, initialData);
        return {
          ...merged,
          id: initialData.id,
          // Use partners from the API response (already a proper array from the relationship)
          partners: initialData.partners?.length
            ? initialData.partners
            : prev.partners?.length ? prev.partners : [blankPartner()],
          services: initialData.telephone_lines
            ? initialData.telephone_lines.map((line) => ({
                id: line.id,
                description: line.number_type?.name || "",
                number_type_id: line.number_type_id,
                connection_option_id: line.connection_option_id,
                existing_tel_no: line.existing_tel_no || "",
                existing_lines: line.existing_lines || "",
                postcode_of_homeworker: line.postcode_of_homeworker || "",
              }))
            : prev.services,
        };
      });
    } else if (mode === "create") {
      // Switching to "create" after having viewed/edited something else —
      // reset so no stale record data lingers in the form.
      setFormData((prev) => ({
        ...prev,
        id: "",
        company_name: "",
        md_name: "",
        position: "",
        company_reg: "",
        registered_address_line_1: "",
        registered_address_line_2: "",
        registered_address_line_3: "",
        registered_address_line_4: "",
        postcode: "",
        installation_address: "",
        director_email: "",
        billing_email: "",
        bank_name: "",
        bank_address: "",
        bank_postcode: "",
        account_holder: "",
        account_number: "",
        sort_code: "",
        partners: [blankPartner()],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, mode]);
  // ── Guard against thin/partial records ──────────────────────────────────
  useEffect(() => {
    const loadFullRecord = async () => {
      if ((mode === "edit" || mode === "view") && initialData?.id) {
        try {
          const res = await connectionService.getConnection(initialData.id);
          setFormData((prev) => mergeNonEmpty(prev, res.data));
        } catch (err) {
          console.error("Failed to load full connection record before edit", err);
          showNotification("Could not load full customer record — some fields may be incomplete", "error");
        }
      }
    };
    loadFullRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, mode]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [serviceRes, numberRes] = await Promise.all([
          connectionService.getServiceTypes(),
          connectionService.getNumberTypes(),
        ]);
        setServiceTypes(serviceRes.data.map((i) => ({ value: i.id, label: i.name })));
        setNumberTypes(numberRes.data.map((i) => ({ value: i.id, label: i.name })));
      } catch (err) {
        console.error("Failed to load service or number types", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setLoadingTeamMembers(true);
      try {
        const res = await TeamService.getTeamMembers();
        console.log(res);
        setTeamMembers(res.data || []);
      } catch (err) {
        console.error("Failed to load team members", err);
      } finally {
        setLoadingTeamMembers(false);
      }
    };
    fetchTeamMembers();
  }, []);

  // ── Salesman ────────────────────────────────────────────────────────────────
  const getSalesmanOptions = () => {
    const opts = [];
    if (userId) opts.push({ value: userId, label: `${userName} (You)`, name: userName, isCurrentUser: true });
    teamMembers
      .filter((m) => String(m.id) !== String(userId))
      .forEach((m) => opts.push({ value: m.id, label: m.name, name: m.name, isCurrentUser: false }));
    return opts;
  };

  const getCurrentSalesmanValue = () => {
    const opts = getSalesmanOptions();
    return opts.find((o) => String(o.value) === String(formData.salesman_id)) || null;
  };

  const handleSalesmanChange = (selected) => {
    if (selected) {
      setFormData((prev) => ({ ...prev, salesman_id: selected.value, salesman_name: selected.name }));
      if (errors.salesman_name) setErrors((prev) => ({ ...prev, salesman_name: "" }));
    }
  };

  // ── Partners ────────────────────────────────────────────────────────────────
  const handlePartnerChange = (index, updated) => {
    setFormData((prev) => {
      const partners = [...prev.partners];
      partners[index] = updated;
      return { ...prev, partners };
    });
  };

  const addPartner = () =>
    setFormData((prev) => ({ ...prev, partners: [...prev.partners, blankPartner()] }));

  const removePartner = (index) =>
    setFormData((prev) => ({
      ...prev,
      partners: prev.partners.filter((_, i) => i !== index),
    }));

  // ── Close ───────────────────────────────────────────────────────────────────
  const onClose = () => {
    if (onCloseProp) onCloseProp();
    else setShowModal(false);
  };

  // ── Input change ────────────────────────────────────────────────────────────
  // IMPORTANT: everything here goes through the functional setState updater.
  // We never read `formData` directly (closure) and then call setFormData
  // again with a plain object — that pattern was overwriting any state
  // changes (Companies House autofill, bank validation autofill, partner
  // edits, etc.) that landed between render and this call, which is what
  // caused data to randomly get wiped.
  const handleInputChange = (field, value) => {
    if (field === "sort_code")      { handleSortCodeChange(value);     return; }
    if (field === "account_number") { handleAccountNumberChange(value); return; }

    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if ([
        "registered_address_line_1",
        "registered_address_line_2",
        "registered_address_line_3",
        "registered_address_line_4",
        "postcode",
      ].includes(field)) {
        next.installation_address = [
          next.registered_address_line_1,
          next.registered_address_line_2,
          next.registered_address_line_3,
          next.registered_address_line_4,
          next.postcode,
        ].filter((l) => l && l.trim() !== "").join(", ");
      }

      return next;
    });

    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ── Companies House ─────────────────────────────────────────────────────────
  const statusStyles = {
    active:   "bg-green-100 text-green-800",
    dissolved:"bg-gray-100 text-gray-800",
    overdue:  "bg-red-100 text-red-800",
    default:  "bg-blue-100 text-blue-800",
  };
  const getStatusStyle = (status) => {
    if (!status) return statusStyles.default;
    if (status.toLowerCase().includes("overdue"))   return statusStyles.overdue;
    if (status.toLowerCase().includes("dissolved")) return statusStyles.dissolved;
    if (status.toLowerCase().includes("active"))    return statusStyles.active;
    return statusStyles.default;
  };

  const searchCompaniesByName = async (term) => {
    if (!term || term.length < 2) { setCompanySearchResults([]); return; }
    setIsSearchingCompany(true);
    try {
      const res = await companiesHouseService.searchCompanies(term, "name");
      if (res.items) { setCompanySearchResults(res.items); setShowCompanyDropdown(true); }
      else setCompanySearchResults([]);
    } catch (err) {
      const isNetworkIssue = !navigator.onLine || (err instanceof TypeError && /fetch/i.test(err.message));
      showNotification(
        isNetworkIssue ? "Offline — can't search Companies House right now" : "Failed to search companies",
        "error"
      );
      setCompanySearchResults([]);
    } finally { setIsSearchingCompany(false); }
  };

  // FIX: use mergeNonEmpty so a Companies House response that omits (or
  // returns null for) fields like director_email, bank details, or
  // installation_address doesn't blank out anything the user already typed.
  const searchCompanyByNumber = async (num) => {
    if (!num || num.length < 3) return;
    setIsSearchingCompany(true);
    try {
      const res = await companiesHouseService.getCompanyDetails(num);
      setFormData((prev) => mergeNonEmpty(prev, res));
      showNotification("Company details loaded successfully", "success");
      setShowCompanyDropdown(false); setCompanySearchTerm("");
    } catch (err) {
      showNotification(
        err.message === "Company not found"
          ? "Company not found with this registration number"
          : "Failed to fetch company details",
        "error"
      );
    } finally { setIsSearchingCompany(false); }
  };

  // FIX: same mergeNonEmpty treatment here.
  const selectCompany = async (company) => {
    setIsSearchingCompany(true);
    try {
      const res = await companiesHouseService.getCompanyDetails(company.company_number);
      setFormData((prev) => mergeNonEmpty(prev, res));
      showNotification("Company details loaded successfully", "success");
      setShowCompanyDropdown(false); setCompanySearchTerm("");
    } catch { showNotification("Failed to fetch company details", "error"); }
    finally { setIsSearchingCompany(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (companySearchMode === "name" && companySearchTerm) searchCompaniesByName(companySearchTerm);
      else if (companySearchMode === "number" && companySearchTerm) searchCompanyByNumber(companySearchTerm);
    }, 500);
    return () => clearTimeout(t);
  }, [companySearchTerm, companySearchMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companySearchRef.current && !companySearchRef.current.contains(e.target))
        setShowCompanyDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Bank validation ─────────────────────────────────────────────────────────
  const validateBankAccountWithAPI = async (sortCode, accountNumber) => {
    const sc = (sortCode || "").toString().replace(/\D/g, "");
    const an = (accountNumber || "").toString().replace(/\D/g, "");
    if (sc.length !== 6 || an.length < 6 || an.length > 8) return;
    setBankValidation((prev) => ({ ...prev, combinedValidation: { ...prev.combinedValidation, loading: true, error: null } }));
    try {
      const res = await bankValidationService.validateBankAccount(sc, an);
      setBankValidation((prev) => ({ ...prev, combinedValidation: { isValid: res.valid, loading: false, data: res.data, error: res.error } }));
      if (res.valid && res.data) {
        const bankName = res.data.bank || res.data.bankname;
        if (bankName) {
          setFormData((prev) => (prev.bank_name ? prev : { ...prev, bank_name: bankName }));
        }
      }
    } catch (err) {
      setBankValidation((prev) => ({
        ...prev,
        combinedValidation: { isValid: false, loading: false, data: null, error: err.message || "Validation service unavailable" },
      }));
    }
  };

  useEffect(() => {
    if (validationDebounce) clearTimeout(validationDebounce);
    const sc = (formData.sort_code || "").toString().replace(/\D/g, "");
    const an = (formData.account_number || "").toString().replace(/\D/g, "");
    if (sc.length === 6 && an.length >= 6 && an.length <= 8) {
      const t = setTimeout(() => validateBankAccountWithAPI(sc, an), 1000);
      setValidationDebounce(t);
    }
    return () => { if (validationDebounce) clearTimeout(validationDebounce); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sort_code, formData.account_number]);

  const validateSortCode    = (v) => {
    if (!v) return { isValid: false, message: "Sort code is required" };
    const c = v.toString().replace(/\D/g, "");
    return c.length !== 6
      ? { isValid: false, message: "Sort code must be 6 digits" }
      : { isValid: true, message: "" };
  };
  const validateAccountNumber = (v) => {
    if (!v) return { isValid: false, message: "Account number is required" };
    const c = v.toString().replace(/\D/g, "");
    return (c.length < 6 || c.length > 8)
      ? { isValid: false, message: "Account number must be between 6-8 digits" }
      : { isValid: true, message: "" };
  };
  const formatSortCode = (v) => {
    if (!v) return "";
    const c = v.replace(/\D/g, "");
    return c.length === 6 ? c.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3") : c;
  };
  const handleSortCodeChange = (value) => {
    const raw = value ? value.replace(/\D/g, "") : "";
    const validation = validateSortCode(raw);
    if (!validation.isValid && raw) setErrors((prev) => ({ ...prev, sort_code: validation.message }));
    else if (errors.sort_code) setErrors((prev) => ({ ...prev, sort_code: "" }));
    setFormData((prev) => ({ ...prev, sort_code: formatSortCode(value || "") }));
    setBankValidation((prev) => ({ ...prev, combinedValidation: { isValid: null, loading: false, data: null, error: null } }));
  };
  const handleAccountNumberChange = (value) => {
    const clean = (value || "").toString().replace(/\D/g, "");
    const validation = validateAccountNumber(clean);
    if (!validation.isValid && clean) setErrors((prev) => ({ ...prev, account_number: validation.message }));
    else if (errors.account_number) setErrors((prev) => ({ ...prev, account_number: "" }));
    setFormData((prev) => ({ ...prev, account_number: clean }));
    setBankValidation((prev) => ({ ...prev, combinedValidation: { isValid: null, loading: false, data: null, error: null } }));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.salesman_name?.trim()) errs.salesman_name = "Salesman is required";
    if (!formData.company_name?.trim())  errs.company_name  = "Company name is required";
    if (!formData.md_name?.trim())       errs.md_name       = "MD name is required";
    if (!formData.director_email?.trim()) errs.director_email = "Director email is required";
    const scv = validateSortCode((formData.sort_code || "").toString().replace(/\D/g, ""));
    if (!scv.isValid && formData.sort_code) errs.sort_code = scv.message;
    const anv = validateAccountNumber(formData.account_number);
    if (!anv.isValid && formData.account_number) errs.account_number = anv.message;
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.director_email && !emailRx.test(formData.director_email)) errs.director_email = "Invalid email format";
    if (formData.billing_email  && !emailRx.test(formData.billing_email))  errs.billing_email  = "Invalid email format";

    if (formData.regulation_status === "NON LIMITED") {
      formData.partners.forEach((p, i) => {
        if (!p.name?.trim()) errs[`partner_${i}_name`]    = "Partner name is required";
        if (!p.dob)          errs[`partner_${i}_dob`]     = "Date of birth is required";
        if (!p.private_address?.trim()) errs[`partner_${i}_address`] = "Private address is required";
      });
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSaveConnection = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        telephone_lines: formData.services.map((svc) => ({
          id: svc.id,
          number_type_id: svc.number_type_id,
          connection_option_id: svc.connection_option_id,
          existing_tel_no: svc.existing_tel_no,
          existing_lines: svc.existing_lines,
          postcode_of_homeworker: svc.postcode_of_homeworker,
        })),
        partners: formData.regulation_status === "NON LIMITED" ? formData.partners : [],
      };
      delete payload.services;

      // FIX: build the "what the form should show after saving" object with
      // mergeNonEmpty instead of a raw spread. Previously
      // `{ ...formData, ...(res.data || {}) }` meant that if the API response
      // omitted a field (e.g. bank details not returned for security, or a
      // relation not eager-loaded on the update response) or returned it as
      // null, that field would get wiped from what's shown on screen and
      // handed to onSaved — which is exactly the "email / bank details /
      // installation address disappear after clicking Update" bug.
      // formData (what the user actually typed) is now the base, and the
      // server response only fills in/overwrites fields it genuinely sent.
      let savedConnection;
      if (mode === "create" || !formData.id) {
        const res = await connectionService.createConnection(payload);
        savedConnection = mergeNonEmpty(formData, res.data);
      } else {
        const res = await connectionService.updateConnection(formData.id, payload);
        savedConnection = mergeNonEmpty(formData, res.data);
      }

      // Keep the in-modal form state in sync with what we're about to hand
      // back up, so if the modal stays open (or is reopened before the
      // parent's refetch completes) the fields the user just saved are still
      // visible rather than reverting to blank.
      setFormData((prev) => mergeNonEmpty(prev, savedConnection));

      showNotification(mode === "create" ? "Connection created successfully" : "Connection updated successfully");
      if (onSaved) onSaved(savedConnection);
      else setShowModal(false);
    } catch (err) {
      console.error("Failed to save connection:", err);

      // fetch() throws a TypeError('Failed to fetch') when the request never
      // reached the server — offline, DNS failure, dropped connection, CORS block.
      const isNetworkIssue =
        !navigator.onLine ||
        (err instanceof TypeError && /fetch/i.test(err.message)) ||
        err?.message === "Network Error" ||        // covers axios if connectionService uses it
        err?.code === "ERR_NETWORK";

      if (isNetworkIssue) {
        showNotification(
          "You appear to be offline or your connection dropped. Nothing was saved — please check your internet connection and click Save again.",
          "error"
        );
      } else {
        showNotification("Failed to save connection. Please try again.", "error");
      }
      // Deliberately do NOT close the modal or call onSaved — the form stays
      // open with everything the user typed still in it, so nothing is lost.
    } finally {
      setLoading(false);
    }
  };

  const isNonLimited = formData.regulation_status === "NON LIMITED";
  const isViewMode   = mode === "view";

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {notification && (
      <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[100] flex items-start gap-4 px-8 py-6 rounded-2xl shadow-2xl text-lg font-semibold max-w-xl w-[90%] border-4 transition-all animate-[fadeIn_0.2s_ease-out]
        ${notification.type === "error"
          ? "bg-red-600 text-white border-red-800"
          : "bg-green-600 text-white border-green-800"}`}
      >
        {notification.type === "error"
          ? <AlertCircle className="w-8 h-8 shrink-0 mt-0.5" />
          : <CheckCircle className="w-8 h-8 shrink-0 mt-0.5" />}
        <span className="leading-snug">{notification.message}</span>
      </div>
    )}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white sticky top-0 z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">
                    {mode === "create"
                      ? "New Digital Quote — Contact Details"
                      : mode === "view"
                      ? "Connection Details"
                      : "Edit Connection"}
                  </h2>
                  {mode === "create" && (
                    <p className="text-blue-100 text-sm mt-1">Step 1 of 2 — Enter contact details, then build the quote</p>
                  )}
                </div>
                <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8 space-y-8">

                  {/* Companies House Search — hidden in view mode */}
                  {!isViewMode && (
                    <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                      <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Building className="w-5 h-5 text-blue-600" />
                        Search Companies House
                      </h2>
                      <div className="flex gap-4">
                        {["name", "number"].map((v) => (
                          <label key={v} className="flex items-center cursor-pointer">
                            <input type="radio" value={v} checked={companySearchMode === v}
                              onChange={(e) => { setCompanySearchMode(e.target.value); setCompanySearchTerm(""); setCompanySearchResults([]); }}
                              className="mr-2" />
                            {v === "name" ? "Search by Company Name" : "Search by Registration Number"}
                          </label>
                        ))}
                      </div>
                      <div className="relative" ref={companySearchRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={companySearchTerm}
                            onChange={(e) => setCompanySearchTerm(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter" && companySearchMode === "number") {
                                e.preventDefault(); searchCompanyByNumber(companySearchTerm);
                              }
                            }}
                            placeholder={companySearchMode === "name"
                              ? "Start typing company name..."
                              : "Enter company registration number (e.g., 12345678)"}
                            className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {isSearchingCompany && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 animate-spin" />}
                        </div>
                        {companySearchMode === "number" && (
                          <button type="button" onClick={() => searchCompanyByNumber(companySearchTerm)}
                            disabled={!companySearchTerm || isSearchingCompany}
                            className="mt-2 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                            {isSearchingCompany
                              ? <><Loader2 className="w-4 h-4 animate-spin" />Searching...</>
                              : <><Search className="w-4 h-4" />Search Company</>}
                          </button>
                        )}
                        {showCompanyDropdown && companySearchResults.length > 0 && companySearchMode === "name" && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {companySearchResults.map((company, i) => (
                              <div key={i} onClick={() => selectCompany(company)}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                                <div className="font-medium text-gray-900">
                                  {company.title}
                                  {company.company_status && (
                                    <span className={`ml-2 px-2 py-1 text-xs rounded-full uppercase ${getStatusStyle(company.company_status)}`}>
                                      {company.company_status}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600">{company.company_number} • {company.address_snippet}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>• Search by company name will show suggestions as you type</p>
                        <p>• Search by registration number requires the exact number (8 digits for UK companies)</p>
                        <p>• Company details will auto-fill the form fields below</p>
                      </div>
                    </div>
                  )}

                  {/* ── Basic Information ── */}
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Basic Information
                    </h2>

                    {formData.company_status && (
                      <div className={`p-4 rounded-lg border ${getStatusStyle(formData.company_status)}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700">Company Status:</span>
                          <span className="font-semibold capitalize">{formData.company_status}</span>
                        </div>
                        {formData.last_filing_date && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="text-sm text-gray-600">
                              Last filing:{" "}
                              <span className="font-medium text-gray-900">
                                {new Date(formData.last_filing_date).toLocaleDateString()}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Salesman *</label>
                        {loadingTeamMembers ? (
                          <div className="animate-pulse h-12 bg-gray-200 rounded-lg" />
                        ) : isViewMode ? (
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                            {formData.salesman_name || "—"}
                          </p>
                        ) : (
                          <Select
                            options={getSalesmanOptions()}
                            value={getCurrentSalesmanValue()}
                            onChange={handleSalesmanChange}
                            placeholder="Search and select salesman..."
                            isSearchable
                            isClearable={false}
                            noOptionsMessage={() => "No salesmen found"}
                            formatOptionLabel={(option) => (
                              <div className="flex items-center justify-between">
                                <span>{option.label}</span>
                                {option.isCurrentUser && <span className="text-xs text-blue-600 font-medium">YOU</span>}
                              </div>
                            )}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "48px",
                                borderColor: errors.salesman_name ? "#ef4444" : state.isFocused ? "#3b82f6" : "#d1d5db",
                                boxShadow: state.isFocused
                                  ? errors.salesman_name ? "0 0 0 2px rgba(239,68,68,0.2)" : "0 0 0 2px rgba(59,130,246,0.2)"
                                  : "none",
                                "&:hover": { borderColor: errors.salesman_name ? "#ef4444" : "#9ca3af" },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
                                color: state.isSelected ? "white" : "#374151",
                              }),
                            }}
                          />
                        )}
                        {errors.salesman_name && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />{errors.salesman_name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Regulation Status</label>
                        {isViewMode ? (
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                            {formData.regulation_status}
                          </p>
                        ) : (
                          <select
                            value={formData.regulation_status}
                            onChange={(e) => handleInputChange("regulation_status", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="LIMITED">Limited</option>
                            <option value="NON LIMITED">Non-Limited</option>
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                        {isViewMode ? (
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                            {formData.contract_date ? new Date(formData.contract_date).toLocaleDateString() : "—"}
                          </p>
                        ) : (
                          <input type="date" value={formData.contract_date}
                            onChange={(e) => handleInputChange("contract_date", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Registration</label>
                        {isViewMode ? (
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                            {formData.company_reg || "—"}
                          </p>
                        ) : (
                          <input type="text" value={formData.company_reg}
                            onChange={(e) => handleInputChange("company_reg", e.target.value)}
                            placeholder="Company registration number"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Partners / Directors (NON LIMITED only) ── */}
                  {isNonLimited && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-orange-500" />
                          Partners / Directors
                          <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full font-medium">
                            Non-Limited
                          </span>
                        </h2>
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={addPartner}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add Partner
                          </button>
                        )}
                      </div>

                      {!isViewMode && (
                        <p className="text-sm text-gray-500">
                          Add details for each partner or director. At least one is required for Non-Limited connections.
                        </p>
                      )}

                      {/* Partner count badge in view mode */}
                      {isViewMode && formData.partners.length > 0 && (
                        <p className="text-sm text-gray-500">
                          {formData.partners.length} partner{formData.partners.length !== 1 ? "s" : ""} / director{formData.partners.length !== 1 ? "s" : ""} on record
                        </p>
                      )}

                      <div className="space-y-3">
                        {formData.partners.map((partner, index) => (
                          <div key={index}>
                            <PartnerCard
                              partner={partner}
                              index={index}
                              onChange={handlePartnerChange}
                              onRemove={removePartner}
                              canRemove={!isViewMode && formData.partners.length > 1}
                              readOnly={isViewMode}
                            />
                            {!isViewMode && (errors[`partner_${index}_name`] || errors[`partner_${index}_dob`] || errors[`partner_${index}_address`]) && (
                              <div className="mt-1 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 space-y-1">
                                {errors[`partner_${index}_name`]    && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_name`]}</p>}
                                {errors[`partner_${index}_dob`]     && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_dob`]}</p>}
                                {errors[`partner_${index}_address`] && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_address`]}</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={addPartner}
                          className="w-full py-3 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 rounded-xl text-sm text-gray-500 hover:text-blue-600 flex items-center justify-center gap-2 transition-all"
                        >
                          <UserPlus className="w-4 h-4" />
                          Add another partner / director
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Company Details ── */}
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                      <Building className="w-5 h-5 text-blue-600" />
                      Company Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        { field: "company_name", label: "Company Name *", placeholder: "Enter company name" },
                        { field: "md_name",       label: "MD Name *",        placeholder: "Managing Director name" },
                        { field: "position",      label: "Position",       placeholder: "Your position" },
                        { field: "postcode",      label: "Postcode",       placeholder: "Postcode" },
                      ].map(({ field, label, placeholder }) => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                          {isViewMode ? (
                            <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                              {formData[field] || "—"}
                            </p>
                          ) : (
                            <input type="text" value={formData[field] || ""}
                              onChange={(e) => handleInputChange(field, e.target.value)}
                              readOnly={field === "company_name" && formData.regulation_status === "LIMITED"}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors[field] ? "border-red-500" : "border-gray-300"} ${field === "company_name" && formData.regulation_status === "LIMITED" ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              placeholder={placeholder} />
                          )}
                          {errors[field] && (
                            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />{errors[field]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Registered Address</label>
                      {isViewMode ? (
                        <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 whitespace-pre-line">
                          {[formData.registered_address_line_1, formData.registered_address_line_2, formData.registered_address_line_3, formData.registered_address_line_4]
                            .filter(Boolean).join("\n") || "—"}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {["registered_address_line_1","registered_address_line_2","registered_address_line_3","registered_address_line_4"].map((field, i) => (
                            <input key={field} type="text" value={formData[field] || ""}
                              onChange={(e) => handleInputChange(field, e.target.value)}
                              readOnly={formData.regulation_status === "LIMITED"}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formData.regulation_status === "LIMITED" ? "bg-gray-100 cursor-not-allowed border-gray-300" : "border-gray-300"}`}
                              placeholder={`Address Line ${i + 1}`} />
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Installation Address</label>
                      {isViewMode ? (
                        <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 whitespace-pre-line">
                          {formData.installation_address || "—"}
                        </p>
                      ) : (
                        <textarea value={formData.installation_address || ""}
                          onChange={(e) => handleInputChange("installation_address", e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Full installation address" />
                      )}
                    </div>
                  </div>

                  {/* ── Contact Information ── */}
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      Contact Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        { field: "director_email", label: "Director's Email *", placeholder: "director@company.com" },
                        { field: "billing_email",  label: "Billing Email",       placeholder: "billing@company.com" },
                      ].map(({ field, label, placeholder }) => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                          {isViewMode ? (
                            <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                              {formData[field] || "—"}
                            </p>
                          ) : (
                            <input type="email" value={formData[field] || ""}
                              onChange={(e) => handleInputChange(field, e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors[field] ? "border-red-500" : "border-gray-300"}`}
                              placeholder={placeholder} />
                          )}
                          {errors[field] && (
                            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />{errors[field]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Banking Details ── */}
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      Banking Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {isViewMode ? (
                        <>
                          {[
                            { field: "bank_name",       label: "Bank Name" },
                            { field: "account_holder",  label: "Account Holder" },
                            { field: "account_number",  label: "Account Number" },
                            { field: "sort_code",        label: "Sort Code" },
                          ].map(({ field, label }) => (
                            <div key={field}>
                              <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                              <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">
                                {formData[field] || "—"}
                              </p>
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                            <input type="text" value={formData.bank_name}
                              onChange={(e) => handleInputChange("bank_name", e.target.value)}
                              placeholder="Bank name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder</label>
                            <input type="text" value={formData.account_holder}
                              onChange={(e) => handleInputChange("account_holder", e.target.value)}
                              placeholder="Account holder name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                            <input type="text" value={formData.account_number}
                              onChange={(e) => handleAccountNumberChange(e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.account_number ? "border-red-500" : bankValidation.combinedValidation.isValid === false ? "border-red-500" : bankValidation.combinedValidation.isValid === true ? "border-green-500" : "border-gray-300"}`}
                              placeholder="6-8 digits" maxLength="8" />
                            {errors.account_number && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.account_number}</p>}
                            {bankValidation.combinedValidation.loading && <p className="text-blue-600 text-sm mt-1 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" />Validating bank account...</p>}
                            {bankValidation.combinedValidation.isValid === true && (
                              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-green-700 text-sm font-medium flex items-center gap-2"><CheckCircle className="w-4 h-4" />Bank Account Validated Successfully</p>
                                {bankValidation.combinedValidation.data?.bank && <p className="text-green-600 text-sm ml-6"><span className="font-medium">Bank:</span> {bankValidation.combinedValidation.data.bank}</p>}
                                {bankValidation.combinedValidation.data?.bic  && <p className="text-green-600 text-sm ml-6"><span className="font-medium">BIC:</span>  {bankValidation.combinedValidation.data.bic}</p>}
                              </div>
                            )}
                            {bankValidation.combinedValidation.isValid === false && (
                              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 text-sm font-medium flex items-center gap-2"><AlertCircle className="w-4 h-4" />Invalid Bank Account Details</p>
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code</label>
                            <input type="text" value={formData.sort_code || ""}
                              onChange={(e) => handleSortCodeChange(e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.sort_code ? "border-red-500" : bankValidation.sortCode.isValid === true ? "border-green-500" : bankValidation.sortCode.isValid === false ? "border-red-500" : "border-gray-300"}`}
                              placeholder="XX-XX-XX format" maxLength="8" />
                            {errors.sort_code && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.sort_code}</p>}
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address & Postcode</label>
                      {isViewMode ? (
                        <div className="space-y-2">
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">{formData.bank_address || "—"}</p>
                          <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800">{formData.bank_postcode || "—"}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <input type="text" value={formData.bank_address}
                            onChange={(e) => handleInputChange("bank_address", e.target.value)}
                            placeholder="Bank address"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          <input type="text" value={formData.bank_postcode}
                            onChange={(e) => handleInputChange("bank_postcode", e.target.value)}
                            placeholder="Bank postcode"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-4 pt-6 border-t mt-6">
                <button type="button" onClick={onClose}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  {isViewMode ? "Close" : "Cancel"}
                </button>
                {!isViewMode && (
                  <button type="button" onClick={handleSaveConnection} disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 disabled:opacity-50">
                    {loading
                      ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Saving...</span></>
                      : <><CheckCircle className="w-5 h-5" /><span>{mode === "create" ? "Save & Build Quote" : "Update"}</span></>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddConnection;